import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";

import BookingsIcon from "@mui/icons-material/Event";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import apiService from "../../../../../service/Api";
import InfoCard from "./InforCard";
import formatDate from "../../../../../utils/FormatDate";
import { BookingResponse } from "../../../../bookings/types/view-booking";

// --- Types ---
interface VenueLeaderboardItem {
  id: number;
  name: string | null;
  booking_count: number;
}

interface PaginatedVenueResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: VenueLeaderboardItem[];
}

// --- Component ---
const MostBookedVenuesLeaderboard: React.FC = () => {
  const [data, setData] = useState<PaginatedVenueResponse>({ results: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedVenue, setSelectedVenue] =
    useState<VenueLeaderboardItem | null>(null);
  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [totalBookings, setTotalBookings] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const navigate = useNavigate();

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch leaderboard
  useEffect(() => {
    const fetchMostBookedVenues = async () => {
      try {
        setLoading(true);
        const res = await apiService.get<PaginatedVenueResponse>(
          "/api/venues/venue/most-booked/"
        );
        setData(res.data ?? { results: [] });
      } catch (err: unknown) {
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to fetch most booked venues.";
        setError(errorMessage);
        setData({ results: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchMostBookedVenues();
  }, []);

  const fetchBookings = useCallback(async (pageNumber: number, pageSize: number) => {
    if (!selectedVenue) return;

    try {
      const res = await apiService.get<{
        count: number;
        results: BookingResponse[];
      }>(
        `/api/bookings/booking/?venue_id=${selectedVenue.id}&page=${
          pageNumber + 1
        }&page_size=${pageSize}`
      );

      setBookings(res.data.results || []);
      setTotalBookings(res.data.count || 0);
    } catch (err) {
      console.error("Failed to fetch bookings", err);
      setBookings([]);
      setTotalBookings(0);
    } finally {
      // no-op
    }
  }, [selectedVenue]);
  useEffect(() => {
    if (selectedVenue) {
      fetchBookings(page, rowsPerPage);
    }
  }, [fetchBookings, page, rowsPerPage, selectedVenue]);

  const handleVenueClick = (venue: VenueLeaderboardItem) => {
    setSelectedVenue(venue);
    setPage(0);
  };

  return (
    <>
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: 0 }}>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Most Booked Venues
        </Typography>

        {loading && <Typography>Loading...</Typography>}
        {error && <Typography color="error">{error}</Typography>}

        <Box sx={{ display: "flex", justifyContent: "space-between", px: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            VENUE
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            BOOKINGS
          </Typography>
        </Box>

        <List>
          {data.results && data.results.length > 0 ? (
            data.results.map((venue, index) => (
              <Box
                key={venue.id}
                onClick={() => handleVenueClick(venue)}
                sx={{ cursor: "pointer" }}
              >
                <ListItem
                  sx={{
                    bgcolor: "#f7f7f7",
                    borderRadius: 2,
                    my: 1,
                    px: 2,
                    py: 1.5,
                    "&:hover": { backgroundColor: "#eaeaea" },
                  }}
                  secondaryAction={
                    <Typography fontWeight={600}>
                      {venue.booking_count ?? 0}
                    </Typography>
                  }
                >
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    sx={{ width: 30 }}
                  >
                    {index + 1}.
                  </Typography>

                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "grey.400" }}>
                      {venue.name?.charAt(0) ?? "V"}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={venue.name ?? "Unnamed venue"}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </Box>
            ))
          ) : (
            <Typography sx={{ textAlign: "center", mt: 2, color: "text.secondary" }}>
              No data available.
            </Typography>
          )}
        </List>
      </Card>

      {/* Modal for venue bookings */}
      <Dialog
        open={!!selectedVenue}
        onClose={() => setSelectedVenue(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{selectedVenue?.name} - Bookings</DialogTitle>

        <DialogContent>
          <InfoCard
            title="Previous Bookings"
            sx={{ height: "100%" }}
            icon={<BookingsIcon />}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : bookings.length === 0 ? (
              <Typography
                variant="h4"
                sx={{
                  fontStyle: "italic",
                  textAlign: "center",
                  py: 4,
                  fontFamily: '"Freehand", cursive',
                }}
              >
                No Related Bookings
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Packege</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {bookings.map((booking, index) => (
                        <TableRow
                          key={booking.id}
                          onClick={() => navigate(`/bookings/${booking.id}`)}
                          sx={{
                            cursor: "pointer",
                            backgroundColor:
                              index % 2 === 0 ? "white" : "#f5f5f5",
                            "&:hover": { backgroundColor: "#e0e0e0" },
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                src={booking?.deceased?.[0]?.person_image || ""}
                                sx={{ width: 32, height: 32 }}
                              >
                                {(
                                  booking?.deceased?.[0]?.first_name?.[0] || ""
                                ).toUpperCase()}
                              </Avatar>
                              <Typography variant="body2">
                                {booking?.deceased?.[0]?.first_name &&
                                booking?.deceased?.[0]?.surname
                                  ? `${booking.deceased[0].first_name} ${booking.deceased[0].surname}`
                                  : booking?.title ?? ""}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{booking.service}</TableCell>
                          <TableCell>{booking.package_item?.name}</TableCell>
                          <TableCell>
                            {formatDate(booking.services?.[0]?.date_of_service)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalBookings}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </>
            )}
          </InfoCard>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MostBookedVenuesLeaderboard;
